import { useEffect, useState } from "react"
import { Container, List, Toggle } from "rsuite"
import CloseIcon from '@rsuite/icons/Close'
import { Button } from "../Controls"
import { useTranslation } from "react-i18next"
import { doQuery } from "../../utils/useQuery"
import UserProjectRelationModel from "@client/models/userProjectRelation"
import RelationName, { RelationType, UserProject } from "./RelationName"
import { Capabilities, hasPermission } from "@/shared"
import { hasRole, RoleType } from "@/shared/roles"

export { RelationType }
export type UserProjectRelationProps = {
    type: RelationType,
    id: string,
    userRole?: RoleType
    buttonText: string,
}

const userProjectRelationModel = new UserProjectRelationModel()

const defaultRelation: UserProject = {
    _id: '',
    user: undefined as any,
    project: undefined as any,
    permissions: {
        read: true,
        write: true,
        delete: false,
    }
}
const getDefaultRelation = (type: RelationType, id: string): UserProject => {
    if (type == RelationType.Project)
        return { ...defaultRelation, ...{ user: id as any } }
    /*if (type == RelationType.User)*/
    return { ...defaultRelation, ...{ project: id as any } }
}

export default function UserProjectRelation({ type, id, userRole, buttonText }: UserProjectRelationProps) {
    const { t } = useTranslation()
    const [page, setPage] = useState<number>(1)
    const [relations, setRelations] = useState<UserProject[]>([])
    const [inputValue, setInputValue] = useState<UserProject>(getDefaultRelation(type, id))
    const [editingIndex, setEditingIndex] = useState<number | null>(null)
    const [deletedRelation, setDeletedRelation] = useState<string>('')
    const canWriteAssign = type == 'project' ? hasPermission(Capabilities.project_write_assign_user) : hasPermission(Capabilities.user_write_assign_project)
    const canDeleteAssign = type == 'project' ? hasPermission(Capabilities.project_delete_assign_user) : hasPermission(Capabilities.user_delete_assign_project)

    const fetchRelations = async () => {
        const ownType = type == RelationType.Project ? RelationType.User : RelationType.Project
        const res = await doQuery(userProjectRelationModel.list, { type: ownType, id, page, count: 20 })
        const data = res.data?.rows || []
        //setRelations(relations => [...relations, ...data])
        setRelations([...relations, ...data])
        setPage(p => p + 1)
    };

    useEffect(() => {
        fetchRelations();
    }, [])

    const saveRelation = async (relation: UserProject) => {
        if (!relation.project || !relation.user) return
        const response = (!relation._id
            ? await userProjectRelationModel.create(relation)
            : await userProjectRelationModel.update(relation._id, relation))
        if (!response?.data) return

        const newRelations = relations.map(item => {
            if (relation._id == item._id) return response.data
            return item
        })
        setRelations(newRelations as any)
    }

    useEffect(() => {
        saveRelation(inputValue)
    }, [inputValue])

    const removeRelation = async () => {
        if (!deletedRelation) return

        const response = await userProjectRelationModel.delete(deletedRelation)
        console.log(response)
        setDeletedRelation('')
    }

    useEffect(() => {
        removeRelation();
    }, [deletedRelation]);

    // for scroll see views/project/in/EditDataList.tsx
    //const handleScroll = (x: number, y: number) => {
    // function handleScroll(event: UIEvent<HTMLElement>): void {
    //     // if (tab?.store?.data?.list?.length == 0) return
    //     // const contextHeight = (tab?.store?.data?.list?.length || 0) * 46;
    //     // const top = Math.abs(y);

    //     // if (contextHeight - top - tableHeight < 300) {
    //     //     setPage(page => page + 1)
    //     //     console.log('scroll: ', page + 1)
    //     // }
    // };

    const addRelation = () => {
        if (editingIndex !== null) {
            const newRelations = [...relations];
            newRelations[editingIndex] = inputValue;
            setRelations(newRelations)
            setEditingIndex(null);
        } else {
            const newRelations = [...relations, inputValue];
            setRelations(newRelations)
            editRelation(relations.length)
        }
        setInputValue(getDefaultRelation(type, id));
    };

    const editRelation = (index: number) => {
        setInputValue(relations[index]);
        setEditingIndex(index);
    };

    const deleteRelation = (index: number) => {
        let deletedItem = ''
        const updated = relations.filter((item, i) => {
            if (i !== index) return true
            deletedItem = item._id
            return false
        })
        setRelations(updated)
        setDeletedRelation(deletedItem)
        setEditingIndex(null)
    };

    const updateInputValue = (value: UserProject, label: string) => {
        setInputValue({ ...value, ...{ label } })
    }

    function toggleCheckbox(permission: string, checked: boolean, index: number): void {
        //let newInputValue: any = { ...inputValue }
        let newInputValue: any = { ...relations[index] }
        newInputValue.permissions[permission] = checked
        setInputValue(newInputValue)
    }

    if (type == 'project' && hasRole(RoleType.Administrator, userRole)) {
        return (
            <span>{t('Assigned to all projects.')}</span>
        )
    }

    return (
        <>
            <List
                key={id}
                className="user-project-relation-list"
            >
                {relations.map((rel, index) => (
                    <List.Item
                        key={index}
                    //onScroll={handleScroll}
                    >
                        <RelationName
                            type={type}
                            relation={editingIndex === index ? inputValue : rel}
                            setInputValue={updateInputValue}

                        />
                        <Toggle
                            size="sm"
                            defaultChecked={rel.permissions.read}
                            checkedChildren={t('Read')}
                            unCheckedChildren={t('Read')}
                            readOnly={!canWriteAssign}
                            onChange={(checked) => toggleCheckbox('read', checked, index)}
                        />
                        <Toggle
                            size="sm"
                            defaultChecked={rel.permissions.write}
                            checkedChildren={t('Write')}
                            unCheckedChildren={t('Write')}
                            readOnly={!canWriteAssign}
                            onChange={(checked) => toggleCheckbox('write', checked, index)}
                        />
                        <Toggle
                            size="sm"
                            defaultChecked={rel.permissions.delete}
                            checkedChildren={t('Delete')}
                            unCheckedChildren={t('Delete')}
                            readOnly={!canWriteAssign}
                            onChange={(checked) => toggleCheckbox('delete', checked, index)}
                        />

                        {canDeleteAssign && <Button className="delete-button rs-btn-close" aria-label={t('Remove')} tabIndex={-1} onClick={() => deleteRelation(index)}><CloseIcon /></Button>}
                    </List.Item>
                ))}
            </List>
            {relations.length == 0 && page <= 1 && <div>{t('Loading ...')}</div>}
            {relations.length == 0 && page != 1 && <div>{type == 'project' ? t('No assigned projects found.') : t('No assigned users found.')}</div>}
            {canWriteAssign && <Container>
                <Button appearance="ghost" onClick={() => { setEditingIndex(null); addRelation() }} style={{ marginTop: '1em', width: 'fit-content' }}>{buttonText}</Button>
            </Container>}
        </>
    )
}